// src/components/admin/scheduling/student_calendar/StudentCreateSessionModal.js

import React, { useEffect, useMemo, useState } from "react";
import SubjectSelect from "../components/SubjectSelect";
import { fetchAvailabilityBlocks, fetchDayConfigsForDates, fetchScheduledSessions } from "../schedulingApi";
import { SATURDAY_BLOCKS, WEEKDAY_BLOCKS } from "../constants";
import { minutesToDate } from "../dateUtils";
import { buildTimeOptions, formatTimeFromMinutes } from "../timeUtils";
import {
  dayKeyFromDate as dayKeyFromScheduleDate,
  defaultTimelineRangeForDayKey as defaultScheduleTimelineRangeForDayKey,
  resolveScheduleForDate,
} from "../scheduleSettingsUtils";

function dayKeyFromDate(d) {
  return dayKeyFromScheduleDate(d);
}

function dateKeyFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function statusKey(status) {
  const s = String(status || "").toLowerCase();
  if (s === "canceled" || s === "cancelled") return "canceled";
  if (s === "confirmed") return "confirmed";
  if (s === "present") return "present";
  return "pending";
}

function minutesOf(dateObj) {
  const d = new Date(dateObj);
  return d.getHours() * 60 + d.getMinutes();
}

function defaultTimelineRange(dayKey) {
  return defaultScheduleTimelineRangeForDayKey(dayKey);
}

function resolveLane(sessions, startMin, endMin) {
  const entries = (sessions || [])
    .filter((session) => {
      if (statusKey(session.status) === "canceled") return false;
      const sessionStart = minutesOf(session.start);
      const sessionEnd = Math.max(sessionStart + 5, minutesOf(session.end));
      return overlaps(sessionStart, sessionEnd, startMin, endMin);
    })
    .map((session, index) => {
      const sessionStart = minutesOf(session.start);
      const sessionEnd = Math.max(sessionStart + 5, minutesOf(session.end));
      return {
        id: session.id || `session_${index}`,
        startMin: sessionStart,
        endMin: sessionEnd,
      };
    });

  entries.push({
    id: "__candidate__",
    startMin,
    endMin,
  });

  entries.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
    if (a.id === "__candidate__") return 1;
    if (b.id === "__candidate__") return -1;
    return 0;
  });

  const laneEnds = [-Infinity, -Infinity, -Infinity];
  const laneById = new Map();

  for (const entry of entries) {
    let lane = null;
    for (let idx = 0; idx < 3; idx += 1) {
      if (laneEnds[idx] <= entry.startMin) {
        lane = idx;
        break;
      }
    }

    if (lane == null) {
      if (entry.id === "__candidate__") return null;
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }

    laneEnds[lane] = Math.max(laneEnds[lane], entry.endMin);
    laneById.set(entry.id, lane);
  }

  return laneById.get("__candidate__") ?? null;
}

function maxConcurrentWithinRange(sessions, startMin, endMin) {
  const events = [];

  (sessions || []).forEach((session) => {
    if (statusKey(session.status) === "canceled") return;
    const s = minutesOf(session.start);
    const e = minutesOf(session.end);
    if (!overlaps(s, e, startMin, endMin)) return;

    const clippedStart = Math.max(s, startMin);
    const clippedEnd = Math.min(e, endMin);
    events.push({ minute: clippedStart, delta: 1 });
    events.push({ minute: clippedEnd, delta: -1 });
  });

  events.sort((a, b) => {
    if (a.minute !== b.minute) return a.minute - b.minute;
    return a.delta - b.delta;
  });

  let current = 0;
  let peak = 0;
  events.forEach((evt) => {
    current += evt.delta;
    if (current > peak) peak = current;
  });

  return peak;
}

function pickBlockId(dayKey, startMin, endMin) {
  const blocks = dayKey === "saturday" ? SATURDAY_BLOCKS : WEEKDAY_BLOCKS;
  if (!blocks.length) return "";

  const exact = blocks.find((block) => startMin >= block.startMin && startMin < block.endMin);
  if (exact) return exact.id;

  let bestBlock = blocks[0];
  let bestOverlap = -1;

  blocks.forEach((block) => {
    const overlapMinutes = Math.max(0, Math.min(endMin, block.endMin) - Math.max(startMin, block.startMin));
    if (overlapMinutes > bestOverlap) {
      bestOverlap = overlapMinutes;
      bestBlock = block;
    }
  });

  return bestBlock?.id || "";
}

export default function StudentCreateSessionModal({
  isOpen,
  onClose,
  student,
  date,
  tutors,
  onCreate,
  initialData = null,
}) {
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [startMin, setStartMin] = useState(0);
  const [endMin, setEndMin] = useState(0);
  const [tutorUid, setTutorUid] = useState("");

  const [dayAvailability, setDayAvailability] = useState([]);
  const [daySessions, setDaySessions] = useState([]);
  const [dayConfig, setDayConfig] = useState(null);
  const [isDayClosed, setIsDayClosed] = useState(false);

  const dayKey = useMemo(() => (date ? dayKeyFromDate(date) : null), [date]);

  const range = useMemo(() => {
    if (!date) {
      const defaults = defaultTimelineRange(dayKey);
      return { startMin: defaults.startMin, endMin: defaults.endMin };
    }
    const resolved = resolveScheduleForDate(date, dayConfig);
    return { startMin: resolved.startMin, endMin: resolved.endMin };
  }, [date, dayConfig, dayKey]);

  useEffect(() => {
    if (!isOpen || !date) return;

    let alive = true;
    (async () => {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const [avail, sess, cfgs] = await Promise.all([
        fetchAvailabilityBlocks({ rangeStart: startOfDay, rangeEnd: endOfDay }),
        fetchScheduledSessions({ rangeStart: startOfDay, rangeEnd: endOfDay }),
        fetchDayConfigsForDates([startOfDay]),
      ]);

      if (!alive) return;

      const cfg = cfgs[dateKeyFromDate(date)] || null;
      const resolved = resolveScheduleForDate(date, cfg);
      setDayAvailability(avail);
      setDaySessions(sess);
      setDayConfig(cfg);
      setIsDayClosed(!!resolved.isClosed);
    })();

    return () => {
      alive = false;
    };
  }, [isOpen, date]);

  useEffect(() => {
    if (!isOpen || !date) return;

    setSubject(initialData?.subject || "");
    setNote(initialData?.note || "");
    setTutorUid(initialData?.tutorUid || "");

    const preferredStart = Number.isFinite(initialData?.startMin) ? initialData.startMin : range.startMin;
    const safeStart = clamp(preferredStart, range.startMin, range.endMin - 5);

    const preferredEnd = Number.isFinite(initialData?.endMin) ? initialData.endMin : safeStart + 110;
    const safeEnd = clamp(Math.max(safeStart + 5, preferredEnd), safeStart + 5, range.endMin);

    setStartMin(safeStart);
    setEndMin(safeEnd);
  }, [isOpen, date, initialData, range.startMin, range.endMin]);

  useEffect(() => {
    if (!isOpen) return;

    if (startMin >= range.endMin) {
      setStartMin(Math.max(range.startMin, range.endMin - 5));
      return;
    }

    if (endMin <= startMin || endMin > range.endMin) {
      const nextEnd = clamp(startMin + 110, startMin + 5, range.endMin);
      setEndMin(nextEnd);
    }
  }, [isOpen, startMin, endMin, range.startMin, range.endMin]);

  const timeOptions = useMemo(() => {
    const latestStart = Math.max(range.startMin, range.endMin - 5);
    const options = buildTimeOptions(range.startMin, latestStart, 5);

    if (!options.includes(startMin)) {
      options.push(startMin);
      options.sort((a, b) => a - b);
    }

    return options;
  }, [range.startMin, range.endMin, startMin]);

  const endOptions = useMemo(() => {
    const earliestEnd = Math.min(range.endMin, startMin + 5);
    const options = buildTimeOptions(earliestEnd, range.endMin, 5);

    if (!options.includes(endMin)) {
      options.push(endMin);
      options.sort((a, b) => a - b);
    }

    return options.filter((value) => value > startMin);
  }, [range.endMin, startMin, endMin]);

  const availableTutors = useMemo(() => {
    if (isDayClosed || !date || endMin <= startMin) return [];

    const sessionStart = minutesToDate(date, startMin);
    const sessionEnd = minutesToDate(date, endMin);

    return (tutors || [])
      .map((tutor) => {
        const tutorBlocks = dayAvailability.filter(
          (block) => block.uid === tutor.id && !block.unavailable
        );

        const isCovered = tutorBlocks.some((block) => {
          const blockStart = new Date(block.start);
          const blockEnd = new Date(block.end);
          return blockStart <= sessionStart && blockEnd >= sessionEnd;
        });

        if (!isCovered) return null;

        const tutorSessions = daySessions.filter(
          (session) => session.tutorUid === tutor.id && statusKey(session.status) !== "canceled"
        );

        const peak = maxConcurrentWithinRange(tutorSessions, startMin, endMin);
        if (peak >= 3) return null;

        const lane = resolveLane(tutorSessions, startMin, endMin);
        if (lane == null) return null;

        return {
          ...tutor,
          seatsLeft: Math.max(0, 3 - peak),
          lane,
        };
      })
      .filter(Boolean);
  }, [tutors, dayAvailability, daySessions, isDayClosed, date, startMin, endMin]);

  useEffect(() => {
    if (!tutorUid) return;
    if (!availableTutors.some((tutor) => tutor.id === tutorUid)) {
      setTutorUid("");
    }
  }, [tutorUid, availableTutors]);

  const handleCreate = () => {
    if (!subject.trim()) {
      alert("Please enter a subject.");
      return;
    }

    if (!tutorUid) {
      alert("Please select a tutor.");
      return;
    }

    if (endMin <= startMin) {
      alert("End time must be after start time.");
      return;
    }

    const selectedTutor = availableTutors.find((tutor) => tutor.id === tutorUid);
    if (!selectedTutor) {
      alert("The selected tutor is unavailable for this time range.");
      return;
    }

    const start = minutesToDate(date, startMin);
    const end = minutesToDate(date, endMin);

    onCreate({
      studentId: student.id,
      studentName: student.name,
      studentGrade: student.grade,
      status: "Pending",
      subject: subject.trim(),
      note: note.trim(),
      tutorUid,
      tutorName: selectedTutor.name || "",
      dayKey,
      blockId: pickBlockId(dayKey, startMin, endMin),
      slotIdx: selectedTutor.lane,
      start,
      end,
    });
  };

  if (!isOpen || !date || !student) return null;

  const dateStr = date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 20,
      }}
      onMouseDown={onClose}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          maxHeight: "85vh",
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e5e7eb",
          boxShadow: "0 12px 40px rgba(15,23,42,0.16)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: "-0.2px" }}>
              {initialData ? "Modify Session" : "Create Session"}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{dateStr}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #e5e7eb", borderRadius: 8,
              width: 28, height: 28, background: "#fff", color: "#374151",
              fontSize: 16, cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {initialData && (
            <div style={{
              marginBottom: 14, fontSize: 13, color: "#b91c1c",
              background: "#fef2f2", padding: "10px 12px",
              borderRadius: 8, border: "1px solid #fecaca",
            }}>
              <strong>Conflict Detected:</strong> The original tutor was unavailable or fully booked. Please select a new tutor or time.
            </div>
          )}

          <div style={MCS.grid}>
            <label style={MCS.label}>Student</label>
            <input
              type="text"
              value={student.name}
              disabled
              style={{ ...MCS.field, background: "#f9fafb", color: "#9ca3af", cursor: "not-allowed" }}
            />

            <label style={MCS.label}>Subject</label>
            <SubjectSelect value={subject} onChange={setSubject} />

            <label style={MCS.label}>Start</label>
            <select value={startMin} onChange={(e) => setStartMin(Number(e.target.value))} style={MCS.field}>
              {timeOptions.map((v) => (
                <option key={v} value={v}>{formatTimeFromMinutes(v)}</option>
              ))}
            </select>

            <label style={MCS.label}>End</label>
            <select value={endMin} onChange={(e) => setEndMin(Number(e.target.value))} style={MCS.field}>
              {endOptions.map((v) => (
                <option key={v} value={v}>{formatTimeFromMinutes(v)}</option>
              ))}
            </select>

            <label style={MCS.label}>Tutor</label>
            <select value={tutorUid} onChange={(e) => setTutorUid(e.target.value)} style={MCS.field}>
              <option value="">— Select Tutor —</option>
              {availableTutors.map((tutor) => (
                <option key={tutor.id} value={tutor.id}>
                  {tutor.name} ({tutor.seatsLeft} seat{tutor.seatsLeft !== 1 ? "s" : ""} left)
                </option>
              ))}
            </select>

            <label style={MCS.label}>Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              style={{ ...MCS.field, resize: "vertical" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={MCS.cancelBtn}>Cancel</button>
          <button
            onClick={handleCreate}
            disabled={isDayClosed}
            style={{ ...MCS.primaryBtn, opacity: isDayClosed ? 0.5 : 1, cursor: isDayClosed ? "not-allowed" : "pointer" }}
          >
            {initialData ? "Save Changes" : "Create Session"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Static styles ────────────────────────────────────────────────────────────

const MCS = {
  grid: {
    display: "grid",
    gridTemplateColumns: "110px 1fr",
    gap: "12px 12px",
    alignItems: "start",
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    paddingTop: 9,
  },
  field: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: "#111827",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },
  cancelBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
  },
  primaryBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #1d4ed8",
    background: "#2563eb",
    fontSize: 13,
    fontWeight: 500,
    color: "#fff",
    cursor: "pointer",
  },
};
